/**
 * KHIYARSA (KMD): Markdown import dialog — paste or pick a .md file, live
 * compile with structure summary + line-level diagnostics, preview, and
 * insertion as native editable elements. Compile/preview/insert machinery is
 * shared with the Create-with-AI dialog (useKmdPreview).
 */
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { useExcalidrawSetAppState } from "@excalidraw/excalidraw/components/App";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAtom, useSetAtom } from "../../../app-jotai";

import { isMaybeKmd } from "../sniff";
import { renderFix, renderMessage, toWireDiagnostic } from "../diagnostics";
import { kmdEditorAtom } from "../editor/atoms";
import { useEditorT } from "../editor/strings";

import { kmdDialogAtom } from "./atoms";
import { useKmdDiagnosticLocale, useKmdT } from "./strings";
import { useKmdPreview } from "./useKmdPreview";

import "./ImportDialog.scss";

export const SEVERITY_COLOR: Record<string, string> = {
  error: "var(--color-danger, #e03131)",
  warning: "#f08c00",
  info: "#868e96",
};

export const KmdImportDialog = () => {
  const t = useKmdT();
  const diagLocale = useKmdDiagnosticLocale();
  const setAppState = useExcalidrawSetAppState();
  const [dialog, setDialog] = useAtom(kmdDialogAtom);
  const setEditorPanel = useSetAtom(kmdEditorAtom);
  const editorT = useEditorT();
  const [text, setText] = useState("");
  const previewRef = useRef<HTMLDivElement | null>(null);
  const { result, insert, reset } = useKmdPreview({
    text,
    active: dialog.open,
    previewRef,
  });

  useEffect(() => {
    if (dialog.open && dialog.initialText !== undefined) {
      setText(dialog.initialText);
    }
  }, [dialog.open, dialog.initialText]);

  const onPickFile = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".md,.markdown,text/markdown";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (file) {
        setText(await file.text());
      }
    };
    input.click();
  }, []);

  const onCopyDiagnostics = useCallback(async () => {
    if (!result) {
      return;
    }
    const payload = {
      valid: result.ok,
      summary: result.summary,
      diagnostics: result.diagnostics.map(toWireDiagnostic),
    };
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
  }, [result]);

  const onDownloadNormalized = useCallback(async () => {
    const { normalizeKmd } = await import("../normalize");
    const normalized = normalizeKmd(text).text;
    const blob = new Blob([normalized], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "diagram.kmd.md";
    a.click();
    URL.revokeObjectURL(url);
  }, [text]);

  const onOpenInEditor = useCallback(() => {
    setEditorPanel({ open: true, initialText: text });
    setDialog({ open: false });
    reset();
    setAppState({ openDialog: null });
  }, [text, setEditorPanel, setDialog, reset, setAppState]);

  const onInsert = useCallback(() => {
    if (!insert()) {
      return;
    }
    setDialog({ open: false });
    setText("");
    reset();
    setAppState({ openDialog: null });
  }, [insert, reset, setDialog, setAppState]);

  if (!dialog.open) {
    return null;
  }

  const summary = result?.summary ?? null;
  const looksLikeKmd = !text.trim() || isMaybeKmd(text);

  return (
    <Dialog
      onCloseRequest={() => {
        setDialog({ open: false });
        setText("");
        reset();
      }}
      title={t("dialogTitle")}
      size="wide"
      className="kmd-import-dialog"
    >
      <div className="kmd-import">
        <div className="kmd-import__left">
          <textarea
            className="kmd-import__source"
            dir="auto"
            spellCheck={false}
            value={text}
            placeholder={t("placeholder")}
            onChange={(event) => setText(event.target.value)}
          />
          <div className="kmd-import__actions">
            <button className="kmd-import__btn" onClick={onPickFile}>
              {t("chooseFile")}
            </button>
            <button
              className="kmd-import__btn"
              disabled={!result}
              onClick={onCopyDiagnostics}
              title={t("copyDiagnostics")}
            >
              {t("copyDiagnostics")}
            </button>
            <button
              className="kmd-import__btn"
              disabled={!text.trim()}
              onClick={onDownloadNormalized}
              title={t("downloadNormalized")}
            >
              {t("downloadNormalized")}
            </button>
            {/* Q6: the import dialog keeps its own one-shot insert AND hands
                off to the live editor — the two workflows stay distinct */}
            <button
              className="kmd-import__btn"
              disabled={!text.trim()}
              onClick={onOpenInEditor}
            >
              {editorT("openInEditor")}
            </button>
            <button
              className="kmd-import__btn kmd-import__btn--primary"
              disabled={!result?.ok}
              onClick={onInsert}
            >
              {t("insert")}
            </button>
          </div>
        </div>
        <div className="kmd-import__right">
          {!looksLikeKmd && (
            <div className="kmd-import__hint">{t("notKmd")}</div>
          )}
          {summary && (
            <div className="kmd-import__summary">
              <strong>{t("summary")}:</strong> {summary.nodes} {t("nodes")} ·{" "}
              {summary.containers} {t("containers")} · {summary.edges}{" "}
              {t("edges")} · {summary.decisions} {t("decisions")} ·{" "}
              {summary.notes} {t("notes")} · {summary.icons} {t("icons")}
            </div>
          )}
          <div className="kmd-import__preview-box">
            <div ref={previewRef} className="kmd-import__preview" />
            {!result?.ok && (
              <div className="kmd-import__no-preview">{t("noPreview")}</div>
            )}
          </div>
          {result && result.diagnostics.length > 0 && (
            <div className="kmd-import__problems">
              <strong>{t("problems")}</strong>
              <ul>
                {result.diagnostics
                  .filter((d) => d.code !== "KMD080")
                  .slice(0, 50)
                  .map((d, i) => (
                    <li key={i} style={{ color: SEVERITY_COLOR[d.severity] }}>
                      <code>{d.code}</code>
                      {d.span ? ` — ${t("line")} ${d.span.line}` : ""}:{" "}
                      {renderMessage(d, diagLocale)}
                      {renderFix(d, diagLocale)
                        ? ` → ${renderFix(d, diagLocale)}`
                        : ""}
                    </li>
                  ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};
